import dayjs from 'dayjs';
import { StoredPreference } from '@frontend/user-preferences';
import { usePreferenceStore, preferenceActions } from '@frontend/user-preferences';
import { useWebsocketEventSubscription } from '@frontend/websocket';

interface UserPreferencesUpdatePayload {
  params: {
    changedUserSettings: Record<
      string,
      {
        updatedAt: string;
        id: string;
        settingValue: string;
      }
    >;
  };
  senderInfo?: {
    client_id?: string;
  };
}

export const useUserPreferencesWebsocketSubscription = () => {
  useWebsocketEventSubscription('USER_PREFERENCES_UPDATE', (rawPayload) => {
    const payload = rawPayload as UserPreferencesUpdatePayload;
    const { clientId } = usePreferenceStore.getState();
    const isSameClient = payload?.senderInfo?.client_id === clientId;

    if (isSameClient) {
      return;
    }

    const changedSettings = payload?.params?.changedUserSettings;
    if (!changedSettings || typeof changedSettings !== 'object') return;

    const updatedPreferences = {} as Record<string, { updatedAt: string; id: string; value: StoredPreference }>;

    const prefs = usePreferenceStore.getState().prefs;
    Object.entries(changedSettings).forEach(([key, rawSettings]) => {
      const setting = rawSettings as { updatedAt: string; id: string; settingValue: string };
      if (!setting?.updatedAt) return;
      const existing = prefs[key];
      const incomingUpdatedAt = dayjs(setting.updatedAt);
      const existingUpdatedAt = existing ? dayjs(existing.updatedAt) : null;
      // Skip if existing is newer or same
      if (existingUpdatedAt && incomingUpdatedAt.isSameOrBefore(existingUpdatedAt)) {
        updatedPreferences[key] = existing;
        return;
      }

      let parsedValue;
      try {
        parsedValue = JSON.parse(setting.settingValue);
      } catch (err) {
        console.error(`Failed to parse settingValue for preference "${key}"`, err);
        return;
      }

      const incomingPref = {
        id: setting?.id || '',
        value: parsedValue as StoredPreference,
        updatedAt: setting.updatedAt,
      };
      updatedPreferences[key] = incomingPref;
    });
    preferenceActions.updatePreferencesFromWebSocket(updatedPreferences);
  });
};
